/**
 * useCreateRole Hook
 *
 * React Query mutation hook for creating roles
 *
 * @module domains/personnel/presentation/hooks/useCreateRole
 */

import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getRoleUseCases } from '@personnel/composition';
import type { Role } from '@personnel/domain/entities/Role';
import type { RoleInputDto } from '@personnel/application/dtos';
import { ROLES_QUERY_KEYS } from './useRoleList';
import { useApiErrorHandler } from './useApiErrorHandler';
import { attachFormFieldError } from '@personnel/application/errors';

/**
 * Input data for creating a role
 * Type alias for RoleInputDto for better semantic naming in this context
 */
export type CreateRoleInput = RoleInputDto;

/**
 * Hook for creating a new role
 *
 * Handles role creation with automatic error handling and toast notifications.
 * Shows success toast on creation and error toast on failure with translated messages.
 *
 * @returns React Query mutation object
 *
 * @example
 * const { mutateAsync: createRole, isLoading } = useCreateRole();
 *
 * const handleSubmit = async (data) => {
 *   try {
 *     await createRole({ name: 'Manager', permissions: [...] });
 *     // Success toast shown automatically
 *     navigate('/roles');
 *   } catch (error) {
 *     // Error toast shown automatically
 *   }
 * };
 */
export const useCreateRole = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('roles');
  const { handleError } = useApiErrorHandler();

  return useMutation<Role, Error, CreateRoleInput>(
    async (dto: CreateRoleInput) => {
      // ✅ Use composed Use Case from Composition Root
      return getRoleUseCases().createRole.execute(dto);
    },
    {
      onSuccess: () => {
        // Invalidate roles list to refetch after creation
        queryClient.invalidateQueries(ROLES_QUERY_KEYS.list());

        // Show success toast
        toast.success(t('notifications.createSuccess'), {
          duration: 3000,
        });
      },

      onError: (error: Error) => {
        // Attach form field metadata if applicable (e.g., "role already exists" → name field)
        attachFormFieldError(error);

        // Show error toast with context (will be skipped for form-handled errors)
        handleError(error, { actionContext: 'create' });
      },
    },
  );
};
